import type { Chat, Adapter, ChatConfig, Message, StateAdapter, Thread } from 'chat';
import { z } from 'zod';

import type { Agent } from '../agent/agent';
import type { MastraProviderMetadata } from '../agent/message-list/state/types';
import type { AgentSignalContents } from '../agent/signals';
import { MastraError } from '../error';
import type { IMastraLogger } from '../logger/logger';
import type { Mastra } from '../mastra';
import type { StorageThreadType } from '../memory/types';
import type {
  InputProcessor,
  InputProcessorOrWorkflow,
  OutputProcessor,
  OutputProcessorOrWorkflow,
} from '../processors';
import { isProcessorWorkflow } from '../processors';
import { RequestContext } from '../request-context';
import type { ApiRoute } from '../server/types';
import type { AgentChunkType } from '../stream/types';
import { createTool } from '../tools/tool';

import { chatModule, getChatModule } from './chat-lazy';
import { resolveSlackTopLevelThreadId } from './compat/slack';
import { ChannelSessionRejectedError } from './errors';

import { formatArgsSummary, formatToolApproved, formatToolDenied, stripToolPrefix } from './formatting';
import {
  buildInlineMediaCheck,
  extractUrls,
  findInlineLinkRule,
  headContentType,
  normalizeInlineLinks,
} from './inline-media';
import type { InlineLinkRule } from './inline-media';
import { ChatChannelOutputProcessor, CHAT_CHANNEL_RENDER_CONTEXT_KEY } from './output-processor';
import type { ChatChannelRenderContext } from './output-processor';
import { ChatChannelProcessor } from './processor';
import { MastraStateAdapter } from './state-adapter';
import { extractErrorMessage } from './stream-helpers';
import type { PendingApprovalRecord } from './stream-helpers';
import type {
  ChannelAdapterConfig,
  ChannelConfig,
  ChannelContext,
  ChannelHandlerContext,
  ChannelHandlers,
  PostableMessage,
  ResolveResourceId,
  ResolveThreadId,
  StreamingConfig,
  ThreadHistoryMessage,
  ToolDisplay,
  ToolDisplayFn,
} from './types';
import { defaultTypingStatus } from './typing-status';
import type { TypingStatusContext, TypingStatusFn } from './typing-status';
import { resolveWaitUntil } from './wait-until';

/** Platforms whose chat-SDK adapters render interactive approval buttons. */
const APPROVAL_BUTTON_PLATFORMS = new Set(['slack', 'discord', 'teams', 'gchat', 'google-chat', 'telegram']);

/**
 * Manages a single Chat SDK instance for an agent, wiring all adapters
 * to the Mastra pipeline (thread mapping → agent.stream → thread.post).
 *
 * One AgentChannels = one bot identity across multiple platforms.
 *
 * @internal Created automatically by the Agent when `channels` config is provided.
 */
export class AgentChannels {
  readonly adapters: Record<string, Adapter>;
  private chat: Chat | null = null;
  /** Stored initialization promise so webhook handlers can await readiness on serverless cold starts. */
  private initPromise: Promise<void> | null = null;
  private agent!: Agent<any, any, any, any>;
  private logger?: IMastraLogger;
  private customState: StateAdapter | undefined;
  private stateAdapter!: StateAdapter;
  private userName: string;
  /** Normalized per-adapter configs (gateway flags, hooks, etc.). */
  private adapterConfigs: Record<string, ChannelAdapterConfig>;
  /** Handler overrides from config. */
  private handlerOverrides: ChannelHandlers;
  /** Additional Chat SDK options. */
  private chatOptions: Omit<ChatConfig, 'adapters' | 'state' | 'userName'>;
  /** Thread context config for fetching prior messages. */
  private threadContext: { maxMessages?: number };
  /** Determines whether a mime type should be sent inline to the model. */
  private shouldInline: (mimeType: string) => boolean;
  /** Inline-link rules for promoting URLs in message text to file parts. */
  private inlineLinkRules: InlineLinkRule[] | undefined;
  /** Whether channel tools (reactions, etc.) are enabled. */
  private toolsEnabled: boolean;
  /** Optional hook to resolve the memory resourceId (owner) for newly-created channel threads. */
  private resolveResourceId: ResolveResourceId | undefined;
  /** Memoized bot display name lookups, keyed by platform. */
  private botDisplayNames = new Map<string, Promise<string | undefined>>();
  /** Optional hook to resolve the internal thread id for newly-created channel threads. */
  private resolveThreadId: ResolveThreadId | undefined;
  /**
   * The original `ChannelConfig` passed to the constructor.
   *
   * Useful for rebuilding `AgentChannels` while preserving existing adapters/handlers,
   * e.g. when a `ChannelProvider` wants to inject its own adapter without clobbering
   * adapters configured by the agent author:
   *
   * @example
   * ```ts
   * const existing = agent.getChannels();
   * const next = new AgentChannels({
   *   ...existing?.channelConfig,
   *   adapters: { ...existing?.channelConfig.adapters, slack: slackAdapter },
   * });
   * agent.setChannels(next);
   * ```
   */
  public readonly channelConfig: ChannelConfig;
  /** Channel tool names whose effects are already visible on the platform (skip rendering cards). */
  private channelToolNames!: Set<string>;
  /** Platforms whose routes are managed externally (e.g., by SlackProvider). */
  private externallyManagedPlatforms: Set<string> = new Set();
  /**
   * Tool-approval cards that have been posted and are awaiting user action. When the user
   * clicks approve/decline, the `onAction` handler looks up the card's `messageId` and
   * tool metadata here so it can edit the card in place and resume the run with the right
   * context. Entries are removed after the resume completes.
   */
  private pendingApprovalCards = new Map<string, PendingApprovalRecord>();

  /**
   * Platforms we've already warned about for misconfigured `toolDisplay` (e.g.
   * `'timeline'` without `streaming: true`). Keeps log output to one warn per
   * platform per AgentChannels instance.
   */
  private warnedToolDisplayFallback = new Set<string>();

  constructor(config: ChannelConfig) {
    // Normalize: extract adapters and per-adapter configs
    const adapters: Record<string, Adapter> = {};
    const adapterConfigs: Record<string, ChannelAdapterConfig> = {};

    for (const [name, value] of Object.entries(config.adapters)) {
      if (value && typeof value === 'object' && 'adapter' in value) {
        const cfg = value as ChannelAdapterConfig;
        adapters[name] = cfg.adapter;
        adapterConfigs[name] = cfg;
      } else {
        adapters[name] = value as Adapter;
        adapterConfigs[name] = { adapter: value as Adapter };
      }
    }

    this.adapters = adapters;
    this.adapterConfigs = adapterConfigs;
    this.handlerOverrides = config.handlers ?? {};
    this.customState = config.state;
    this.userName = config.userName ?? 'Mastra';
    this.chatOptions = config.chatOptions ?? {};
    this.threadContext = config.threadContext ?? {};
    this.shouldInline = buildInlineMediaCheck(config.inlineMedia);
    this.inlineLinkRules = normalizeInlineLinks(config.inlineLinks);
    this.toolsEnabled = config.tools !== false;
    this.resolveResourceId = config.resolveResourceId;
    this.resolveThreadId = config.resolveThreadId;
    this.channelConfig = config;
    this.channelToolNames = new Set(Object.keys(this.getTools()));
  }

  /**
   * Bind this AgentChannels to its owning agent. Called by Agent constructor.
   * @internal
   */
  __setAgent(agent: Agent<any, any, any, any>): void {
    this.agent = agent;
  }

  /**
   * Set the logger. Called by Mastra.addAgent.
   * @internal
   */
  __setLogger(logger: IMastraLogger): void {
    this.logger =
      'child' in logger && typeof (logger as any).child === 'function' ? (logger as any).child('CHANNEL') : logger;
  }

  /**
   * Register an adapter dynamically.
   * When `managesRoutes` is true, AgentChannels will NOT create webhook routes for this platform
   * (the ChannelProvider handles routing and calls handleWebhookEvent directly).
   * @internal
   */
  __registerAdapter(
    platform: string,
    adapter: Adapter,
    config?: ChannelAdapterConfig,
    options?: { managesRoutes?: boolean },
  ): void {
    if (this.adapters[platform]) {
      if (options?.managesRoutes) {
        this.externallyManagedPlatforms.add(platform);
      }
      return;
    }
    this.adapters[platform] = adapter;
    this.adapterConfigs[platform] = config ?? { adapter };
    if (options?.managesRoutes) {
      this.externallyManagedPlatforms.add(platform);
    }
  }

  // -------------------------------------------------------------------------
  // Protected dispatch seams
  //
  // These are the exact points where inbound platform events are routed into
  // the owning agent. A subclass can override them to route events into a
  // different execution surface while reusing all of the shared machinery
  // (thread mapping, event/render context, approval cards, drivers).
  // -------------------------------------------------------------------------

  /**
   * Id of the entity that owns this channels instance, used in webhook route
   * paths. Returns `null` when no owner is bound yet, in which case
   * `getWebhookRoutes()` returns no routes.
   */
  protected getOwnerId(): string | null {
    return this.agent?.id ?? null;
  }

  /** Base path for webhook routes, e.g. `/api/agents/{agentId}`. */
  protected getWebhookBasePath(): string {
    return `/api/agents/${this.getOwnerId()}`;
  }

  /** Resolve the Mastra instance from the bound owner. */
  protected getMastra(): Mastra | undefined {
    return this.agent?.getMastraInstance();
  }

  /**
   * The memory resourceId (owner) used when `processChatMessage` creates a new
   * channel-backed thread. Returns a thunk when a `resolveResourceId` hook is
   * configured so the hook only runs when a new thread is actually created,
   * never when reusing an existing one (which keeps its stored owner).
   */
  protected resolveChannelResourceId(args: {
    platform: string;
    chatThread: Thread;
    message: Message;
    defaultResourceId: string;
  }): string | (() => string | Promise<string>) {
    const { platform, chatThread, message, defaultResourceId } = args;
    return this.resolveResourceId
      ? () => this.resolveResourceId!({ platform, thread: chatThread, message, defaultResourceId })
      : defaultResourceId;
  }

  /**
   * Route an inbound chat message into the owning agent's signal pipeline.
   * The message either gets delivered into an already-running agent loop or
   * wakes the thread with an idle stream.
   */
  protected async dispatchInboundMessage(args: {
    signalContents: AgentSignalContents;
    attributes: Record<string, string | undefined>;
    signalMetadata: Record<string, unknown>;
    providerOptions: MastraProviderMetadata;
    requestContext: RequestContext;
    /** The mapped Mastra thread for the chat thread this message arrived on. */
    thread: StorageThreadType;
    memory: { thread: string; resource: string };
    /** Set when the adapter can't render approval buttons, to avoid runs parking forever. */
    autoResumeSuspendedTools: true | undefined;
  }): Promise<void> {
    const {
      signalContents,
      attributes,
      signalMetadata,
      providerOptions,
      requestContext,
      memory,
      autoResumeSuspendedTools,
    } = args;

    const result = this.agent.sendMessage(
      {
        contents: signalContents,
        attributes,
        ...(Object.keys(signalMetadata).length > 0 ? { metadata: signalMetadata } : {}),
        providerOptions,
      },
      {
        resourceId: memory.resource,
        threadId: memory.thread,
        ifIdle: {
          behavior: 'wake',
          streamOptions: {
            requestContext,
            memory,
            // Without approval-button rendering, auto-approve tools to
            // avoid getting stuck waiting for input we can't ask for.
            ...(autoResumeSuspendedTools ? { autoResumeSuspendedTools } : {}),
          },
        },
      },
    );

    // `accepted` rejects when the agent throws before a run exists (workspace,
    // instructions, tools, or model resolution). Nothing was persisted and no
    // run will render the failure, so let it propagate to the channel error
    // boundary rather than silently dropping the message.
    const accepted = await result.accepted;

    // When this call wakes a new run, drive it to completion before returning.
    // Without this, serverless runtimes (Vercel, Lambda, etc.) terminate the
    // invocation as soon as the webhook handler returns and kill the run
    // mid-flight. `consumeStream()` is idempotent and safe to call alongside
    // the existing per-thread subscription consumer.
    //
    // Only the `wake` action means this process started and owns the run.
    // Any other action (deliver/persist/discard) handed the signal off, so
    // there is nothing to drive to completion here.
    if (accepted.action === 'wake') {
      try {
        await accepted.output.consumeStream();
      } catch (err) {
        // The run already started; the output processor reports its failure.
        this.log('debug', 'accepted consume failed', err);
      }
    } else {
      this.log(
        accepted.action === 'deliver' ? 'debug' : 'warn',
        `[dispatchInboundMessage] inbound message did not start a run (action: ${accepted.action}); the thread may be suspended awaiting tool approval`,
        { threadId: memory.thread, resourceId: memory.resource },
      );
    }
  }

  /**
   * Resume a suspended run with an approval and drive the resumed stream to
   * completion (serverless safety).
   */
  protected async dispatchApproval(args: {
    runId: string;
    toolCallId: string;
    requestContext: RequestContext;
    memory: { thread: string; resource: string };
  }): Promise<void> {
    const resumed = await this.agent.approveToolCall({
      runId: args.runId,
      toolCallId: args.toolCallId,
      requestContext: args.requestContext,
      memory: args.memory,
    });
    // Drive the run to completion so serverless runtimes don't kill it.
    void resumed.consumeStream().catch(err => {
      this.log('error', 'Error consuming resumed approval stream', err);
    });
  }

  /**
   * Resume a suspended run with a denial and drive the resumed stream to
   * completion (serverless safety).
   */
  protected async dispatchDecline(args: {
    runId: string;
    toolCallId: string;
    requestContext: RequestContext;
    memory: { thread: string; resource: string };
  }): Promise<void> {
    const resumed = await this.agent.declineToolCall({
      runId: args.runId,
      toolCallId: args.toolCallId,
      requestContext: args.requestContext,
      memory: args.memory,
    });
    // Drive the run to completion so serverless runtimes don't kill it.
    void resumed.consumeStream().catch(err => {
      this.log('error', 'Error consuming resumed decline stream', err);
    });
  }

  /**
   * Check if an adapter is registered for the given platform.
   */
  hasAdapter(platform: string): boolean {
    return platform in this.adapters;
  }

  /**
   * Get the underlying Chat SDK instance.
   * Available after Mastra initialization. Use this to register additional
   * event handlers or access adapter-specific methods.
   *
   * @example
   * ```ts
   * agent.channels.sdk.onReaction((thread, reaction) => {
   *   console.log('Reaction received:', reaction);
   * });
   * ```
   */
  get sdk(): Chat | null {
    return this.chat;
  }

  /**
   * Initialize the Chat SDK, register handlers, and start gateway listeners.
   * Called by Mastra.addAgent after the server is ready.
   */
  async initialize(mastra: Mastra): Promise<void> {
    if (this.chat) return;
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      // Resolve state adapter: custom > Mastra storage > in-memory fallback
      if (this.customState) {
        this.stateAdapter = this.customState;
      } else {
        const storage = mastra.getStorage();
        const memoryStore = storage ? await storage.getStore('memory') : undefined;
        if (!memoryStore) {
          throw new Error(
            'Channels require storage to be configured on the Mastra instance. Configure a storage provider like LibSQLStore.',
          );
        }
        this.stateAdapter = new MastraStateAdapter(memoryStore, () => this.getOwnerId());
        this.log('info', 'Using MastraStateAdapter (subscriptions persist across restarts)');
      }

      const { Chat, Message: ChatMessage, ThreadImpl } = await getChatModule();
      const chat = new Chat({
        adapters: this.adapters,
        state: this.stateAdapter,
        userName: this.userName,
        // Dispatch every incoming message immediately. Concurrency and queueing
        // for the same thread are handled by the agent signals layer
        // (ifActive/ifIdle behaviors), so chat-sdk's own lock-based queue would
        // be redundant — and in serverless runtimes a stale lock from a frozen
        // Lambda can cause subsequent messages to be queued forever.
        concurrency: { strategy: 'concurrent' },
        ...this.chatOptions,
      });

      // Register handlers with optional overrides
      const { onDirectMessage, onMention, onSubscribedMessage, onSlashCommand, onAction } = this.handlerOverrides;

      // Per-message dispatch scope. The request context and the handler context
      // MUST be built per message, never once at initialize() time: a custom
      // handler may write the sender's tenant onto the request context, and a
      // shared instance would leak that tenant into the next message's run.
      // `skipped` holds earlier messages the Chat SDK batched into this dispatch
      // under a `burst`/`debounce`/`queue` concurrency strategy (oldest first).
      const beginMessage = (skipped: readonly Message[] = []) => {
        const requestContext = new RequestContext();
        const signalMetadata: Record<string, unknown> = {};
        const defaultHandler = (chatThread: Thread, message: Message) =>
          this.handleChatMessage(chatThread, message, mastra, requestContext, signalMetadata, skipped);
        // Context handed to custom handlers so they can reach the resolved Mastra
        // instance without being injected with an external accessor, and
        // contribute to the request context the run will dispatch with.
        const handlerContext: ChannelHandlerContext = { mastra, requestContext, signalMetadata, skipped };
        return { defaultHandler, handlerContext };
      };

      if (onDirectMessage !== false) {
        chat.onDirectMessage((thread, message, _channel, context) => {
          const { defaultHandler, handlerContext } = beginMessage(context?.skipped);
          if (typeof onDirectMessage === 'function') {
            return onDirectMessage(thread, message, defaultHandler, handlerContext);
          }
          return defaultHandler(thread, message);
        });
      }

      if (onMention !== false) {
        chat.onNewMention((thread, message, context) => {
          const { defaultHandler, handlerContext } = beginMessage(context?.skipped);
          if (typeof onMention === 'function') {
            return onMention(thread, message, defaultHandler, handlerContext);
          }
          return defaultHandler(thread, message);
        });
      }

      if (onSubscribedMessage !== false) {
        chat.onSubscribedMessage((thread, message, context) => {
          const { defaultHandler, handlerContext } = beginMessage(context?.skipped);
          if (typeof onSubscribedMessage === 'function') {
            return onSubscribedMessage(thread, message, defaultHandler, handlerContext);
          }
          return defaultHandler(thread, message);
        });
      }

      if (onSlashCommand !== false) {
        chat.onSlashCommand(event => {
          const { defaultHandler: handleMessage, handlerContext } = beginMessage();
          const defaultHandler = async () => {
            const text = `${event.command} ${event.text}`.trim();
            const threadId = event.channel.id;
            const message = new ChatMessage({
              attachments: [],
              author: event.user,
              formatted: {
                type: 'root',
                children: [{ type: 'paragraph', children: [{ type: 'text', value: text }] }],
              },
              id: event.triggerId ?? crypto.randomUUID(),
              metadata: { dateSent: new Date(), edited: false },
              raw: event.raw,
              text,
              threadId,
            });
            const thread = new ThreadImpl({
              adapter: event.adapter,
              channelId: event.channel.id,
              channelVisibility: event.channel.channelVisibility,
              currentMessage: message,
              id: threadId,
              isDM: event.channel.isDM,
              stateAdapter: this.stateAdapter,
            });
            return handleMessage(thread, message);
          };

          if (typeof onSlashCommand === 'function') {
            return onSlashCommand(event, defaultHandler, handlerContext);
          }
          return defaultHandler();
        });
      }

      if (onAction !== false) {
        chat.onAction(event => {
          const { handlerContext } = beginMessage();
          // Tool approval buttons — id is "tool_approve:<toolCallId>" or "tool_deny:<toolCallId>"
          const defaultHandler = async () => {
            const { actionId } = event;
            if (!actionId.startsWith('tool_approve:') && !actionId.startsWith('tool_deny:')) return;
            try {
              const approved = actionId.startsWith('tool_approve:');
              const toolCallId = actionId.split(':')[1];
              if (!toolCallId) {
                this.log('info', `Missing toolCallId in action event actionId=${actionId}`);
                return;
              }

              const chatThread = event.thread as Thread | null;
              if (!chatThread) {
                this.log('info', `No thread in action event for toolCallId=${toolCallId}`);
                return;
              }
              const platform = event.adapter.name;
              const messageId = event.messageId;
              const adapter = this.adapters[platform];
              const adapterConfig = this.adapterConfigs[platform];
              if (!adapter) throw new Error(`No adapter for platform "${platform}"`);

              const externalThreadId = this.resolveExternalThreadId({ platform, chatThread, messageId });
              const { thread: mastraThread } = await this.findThreadMapping({
                externalThreadId,
                channelId: chatThread.channelId,
                platform,
                mastra,
              });
              if (!mastraThread) {
                // Approval cards can only continue runs on threads created by an
                // earlier message. Do not mint a replacement from the clicker's
                // identity when that durable mapping is missing.
                this.log('warn', `No mapped channel thread found for tool approval action toolCallId=${toolCallId}`);
                return;
              }

              // Look up the runId for this toolCallId. Prefer the in-memory
              // `pendingApprovalCards` map (set when the approval card was posted)
              // because it's keyed by toolCallId and survives parallel same-tool
              // approvals. Fall back to the persisted `pendingToolApprovals`
              // metadata for cases where the bot restarted between card post and
              // click (the metadata path is lossy for parallel same-tool calls
              // since core keys those by toolName — only the latest survives).
              let runId: string | undefined;
              let toolName: string | undefined;
              let toolArgs: Record<string, unknown> | undefined;

              const stashed = this.pendingApprovalCards.get(toolCallId);
              let requesterId = stashed?.requesterId;
              if (stashed?.runId) {
                runId = stashed.runId;
                toolName = stashed.toolName;
                toolArgs = stashed.args;
              } else {
                const storage = mastra.getStorage();
                const memoryStore = storage ? await storage.getStore('memory') : undefined;
                if (!memoryStore) {
                  throw new Error('Storage is required for tool approval lookups');
                }

                const { messages } = await memoryStore.listMessages({
                  threadId: mastraThread.id,
                  perPage: 50,
                  orderBy: { field: 'createdAt', direction: 'DESC' },
                });

                for (const [index, msg] of messages.entries()) {
                  const pending = msg.content?.metadata?.pendingToolApprovals as
                    | Record<
                        string,
                        {
                          toolCallId: string;
                          runId: string;
                          parentRunId?: string;
                          toolName: string;
                          args: Record<string, unknown>;
                        }
                      >
                    | undefined;
                  if (pending) {
                    for (const toolData of Object.values(pending)) {
                      if (toolData.toolCallId === toolCallId) {
                        runId = toolData.parentRunId ?? toolData.runId;
                        toolName = toolData.toolName;
                        toolArgs = toolData.args;
                        // Recover the card's owner from the user turn that led to it
                        // (messages are newest-first). If that turn has messages from
                        // more than one author we can't tell who triggered the tool,
                        // so no one may answer the card.
                        const earlier = messages.slice(index + 1);
                        const turnStart = earlier.findIndex(m => m.role === 'user');
                        const turnEnd = earlier.findIndex((m, i) => i > turnStart && m.role !== 'user');
                        const authors = new Set(
                          (turnStart === -1 ? [] : earlier.slice(turnStart, turnEnd === -1 ? undefined : turnEnd))
                            .map(
                              m =>
                                (
                                  m.content?.providerMetadata?.mastra as
                                    | { channels?: Record<string, { author?: { userId?: string } }> }
                                    | undefined
                                )?.channels?.[platform]?.author?.userId,
                            )
                            .filter((id): id is string => !!id),
                        );
                        if (!requesterId && authors.size > 1) {
                          this.log(
                            'info',
                            `Ignoring tool approval action: requester for toolCallId=${toolCallId} is ambiguous`,
                          );
                          return;
                        }
                        requesterId ??= [...authors][0];
                        break;
                      }
                    }
                    if (runId) break;
                  }
                }
              }

              if (!runId) {
                this.log('info', `No pending approval found for toolCallId=${toolCallId}`);
                return;
              }

              // Only the user whose message triggered the tool call may answer
              // its approval card. Skip the check when either identity is unknown.
              const actorId = event.user?.userId;
              if (requesterId && actorId && requesterId !== actorId) {
                this.log(
                  'info',
                  `Ignoring tool approval action from ${actorId}: only ${requesterId} may answer toolCallId=${toolCallId}`,
                );
                return;
              }

              // Build the card header with tool name and args
              const displayName = toolName ? stripToolPrefix(toolName) : 'tool';
              const argsSummary = toolArgs ? formatArgsSummary(toolArgs) : '';
              // Resolve the tool display mode so the approve/deny edit matches
              // the original card's rendering (cards → Block Kit, text → plain).
              // Streaming is irrelevant here — we're outside the agent loop.
              const { resolved: toolDisplay, fn: toolDisplayFn } = this.resolveToolDisplay(
                platform,
                adapterConfig?.toolDisplay,
                false,
                adapterConfig?.cards,
                adapterConfig?.formatToolCall,
              );
              const useCards = toolDisplay === 'cards';
              // Let a function-form `toolDisplay` own the resolved card. Only
              // non-blank `post` results are honored; anything else (or a
              // throwing renderer) falls back to the default formatter.
              const renderResolved = (
                decision: { kind: 'approved' } | { kind: 'denied'; byUser?: string },
              ): PostableMessage | undefined => {
                if (!toolDisplayFn) return undefined;
                try {
                  const result = toolDisplayFn(
                    {
                      ...decision,
                      toolCallId,
                      toolName: toolName ?? displayName,
                      displayName,
                      argsSummary,
                      args: toolArgs,
                    },
                    { mode: 'static', platform },
                  );
                  if (result?.kind !== 'post' || result.message == null) return undefined;
                  const message = result.message;
                  const blank =
                    typeof message === 'string'
                      ? message.trim().length === 0
                      : 'markdown' in message && message.markdown.trim().length === 0;
                  return blank ? undefined : message;
                } catch (err) {
                  this.log('debug', `toolDisplay threw for ${decision.kind} event`, err);
                  return undefined;
                }
              };

              if (!approved) {
                const byUser = chatThread.isDM ? undefined : event.user.fullName || event.user.userName || 'User';
                try {
                  await adapter.editMessage(
                    chatThread.id,
                    messageId,
                    renderResolved({ kind: 'denied', byUser }) ??
                      formatToolDenied(displayName, argsSummary, byUser, useCards),
                  );
                } catch (err) {
                  this.log('debug', 'Failed to edit denied card', err);
                }

                // Resume the suspended run with a denial so the agent can produce a
                // follow-up message (e.g. acknowledging the rejection). Stash the
                // render context so `ChatChannelOutputProcessor` renders the output
                // inline — same path as processChatMessage and the approve branch.
                const { channelContext } = await this.buildEventContext({
                  chatThread,
                  platform,
                  eventType: 'action',
                  messageId,
                  actor: event.user,
                });
                const { requestContext } = handlerContext;
                requestContext.set('channel', channelContext);

                const renderContext = this._buildRenderContext(chatThread, platform, { requesterId });
                requestContext.set(CHAT_CHANNEL_RENDER_CONTEXT_KEY, renderContext);

                try {
                  await this.dispatchDecline({
                    runId,
                    toolCallId,
                    requestContext,
                    memory: {
                      thread: mastraThread.id,
                      resource: mastraThread.resourceId,
                    },
                  });
                } catch (err) {
                  const isStaleApproval = err instanceof Error && err.message.includes('No snapshot found');
                  if (isStaleApproval) {
                    this.log('info', `Ignoring stale tool denial action (runId already consumed)`);
                  } else {
                    throw err;
                  }
                } finally {
                  // Stash entry is no longer needed; the resumed decline stream
                  // won't emit a tool-result for this call.
                  this.pendingApprovalCards.delete(toolCallId);
                }
                return;
              }

              // Immediately edit the card to show "Approved" and remove the buttons
              try {
                await adapter.editMessage(
                  chatThread.id,
                  messageId,
                  renderResolved({ kind: 'approved' }) ?? formatToolApproved(displayName, argsSummary, useCards),
                );
              } catch (err) {
                this.log('debug', 'Failed to edit approved card', err);
              }

              // Build request context for the resumed stream. Stash the render
              // context so `ChatChannelOutputProcessor` renders the tool-result
              // and any follow-up output inline — same path as processChatMessage.
              const { channelContext } = await this.buildEventContext({
                chatThread,
                platform,
                eventType: 'action',
                messageId,
                actor: event.user,
              });
              const { requestContext } = handlerContext;
              requestContext.set('channel', channelContext);

              const renderContext = this._buildRenderContext(chatThread, platform, {
                approvalContext: { toolCallId, messageId },
                requesterId,
              });
              requestContext.set(CHAT_CHANNEL_RENDER_CONTEXT_KEY, renderContext);

              await this.dispatchApproval({
                runId,
                toolCallId,
                requestContext,
                memory: {
                  thread: mastraThread.id,
                  resource: mastraThread.resourceId,
                },
              });
            } catch (err) {
              const isStaleApproval = err instanceof Error && err.message.includes('No snapshot found');
              if (isStaleApproval) {
                this.log('info', `Ignoring stale tool approval action (runId already consumed)`);
                return;
              }
              // The resolver also runs on approval continuations, so a refusal
              // here means this clicker isn't allowed to act — same silence as the
              // inbound path (see handleChatMessage).
              if (err instanceof ChannelSessionRejectedError) {
                this.log('info', 'Session resolver refused the tool approval action', { reason: err.message });
                return;
              }
              this.log('error', 'Error handling tool approval action', err);
              try {
                const thread = event.thread;
                if (thread) {
                  const error = err instanceof Error ? err : new Error(String(err));
                  const adapterConfig = this.adapterConfigs[event.adapter.name];
                  const errorMessage = adapterConfig?.formatError
                    ? adapterConfig.formatError(error)
                    : `❌ Error: ${error.message}`;
                  await thread.post(errorMessage);
                }
              } catch (err) {
                this.log('debug', 'Failed to post error message for action', err);
              }
            }
          };

          if (typeof onAction === 'function') {
            return onAction(event, defaultHandler, handlerContext);
          }
          return defaultHandler();
        });
      }
      await chat.initialize();
      this.chat = chat;

      // Start gateway listeners for adapters that support it (e.g. Discord)
      for (const [name, adapter] of Object.entries(this.adapters)) {
        if (!(this.adapterConfigs[name]?.gateway ?? true)) continue;

        const adapterAny = adapter as unknown as Record<string, unknown>;
        if (typeof adapterAny.startGatewayListener === 'function') {
          const startGateway = adapterAny.startGatewayListener.bind(adapter) as (
            options: { waitUntil: (p: Promise<unknown>) => void },
            durationMs?: number,
          ) => Promise<Response>;

          this.startGatewayLoop(name, startGateway);
        }
      }
    })();

    try {
      await this.initPromise;
    } catch (error) {
      this.initPromise = null;
      throw error;
    }
  }

  /**
   * Returns API routes for receiving webhook events from each adapter.
   * One POST route per adapter at `/api/agents/{agentId}/channels/{platform}/webhook`.
   * Skips platforms that are externally managed (e.g., by SlackProvider).
   */
  getWebhookRoutes(): ApiRoute[] {
    if (!this.getOwnerId()) return [];

    const basePath = this.getWebhookBasePath();
    const routes: ApiRoute[] = [];

    for (const platform of Object.keys(this.adapters)) {
      // Skip platforms where routes are managed externally (e.g., SlackProvider)
      if (this.externallyManagedPlatforms.has(platform)) {
        continue;
      }
      const self = this;
      routes.push({
        path: `${basePath}/channels/${platform}/webhook`,
        method: 'POST',
        requiresAuth: false,
        _mastraInternal: true,
        cors: this.adapterConfigs[platform]?.cors,
        createHandler: async () => {
          return async c => {
            // Await initialization to handle serverless cold starts where
            // the first request arrives before initialize() completes.
            if (self.initPromise) {
              try {
                await self.initPromise;
              } catch {
                return c.json({ error: 'Chat initialization failed' }, 503);
              }
            }

            const sdkInstance = self.chat;
            if (!sdkInstance) {
              return c.json({ error: 'Chat not initialized' }, 503);
            }
            // `webhooks` is an internal Chat SDK property (not in public typings)
            const webhookHandler = (sdkInstance as any).webhooks?.[platform] as Function | undefined;
            if (!webhookHandler) {
              return c.json({ error: `No webhook handler for ${platform}` }, 404);
            }

            // Pass platform execution context (e.g. Vercel/Cloudflare waitUntil)
            // to the Chat SDK so background processing survives serverless responses.
            // Resolution order: bare `waitUntil` fn from config → user resolver → default.
            const waitUntilFn =
              self.channelConfig.waitUntil ?? self.channelConfig.resolveWaitUntil?.(c) ?? resolveWaitUntil(c);
            return webhookHandler(c.req.raw, waitUntilFn ? { waitUntil: waitUntilFn } : undefined);
          };
        },
      });
    }

    return routes;
  }

  /**
   * Handle a webhook event from an external source (e.g., SlackProvider).
   * Use this when a ChannelProvider manages its own routes but wants AgentChannels
   * to process the actual message handling (threading, agent responses, etc.).
   *
   * @param platform - The platform name (e.g., 'slack')
   * @param request - The raw HTTP request
   * @param options - Optional execution context for serverless environments
   * @returns The response from the Chat SDK webhook handler
   */
  async handleWebhookEvent(
    platform: string,
    request: Request,
    options?: { waitUntil?: (p: Promise<unknown>) => void },
  ): Promise<Response> {
    // Ensure initialization is complete
    if (this.initPromise) {
      try {
        await this.initPromise;
      } catch {
        return new Response(JSON.stringify({ error: 'Channel initialization failed' }), {
          status: 503,
          headers: { 'Content-Type': 'application/json' },
        });
      }
    }

    const sdkInstance = this.chat;
    if (!sdkInstance) {
      return new Response(JSON.stringify({ error: 'Chat not initialized' }), {
        status: 503,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    // Access the internal webhook handler from Chat SDK
    const webhookHandler = (sdkInstance as any).webhooks?.[platform] as Function | undefined;
    if (!webhookHandler) {
      return new Response(JSON.stringify({ error: `No webhook handler for ${platform}` }), {
        status: 404,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    return webhookHandler(request, options);
  }

  /**
   * Returns channel input processors (e.g. system prompt injection).
   *
   * - Skipped entirely when `channels.threadContext.addSystemMessage` is `false`.
   * - Skipped if the user already added a processor with the same id.
   */
  getInputProcessors(configuredProcessors: InputProcessorOrWorkflow[] = []): InputProcessor[] {
    if (this.channelConfig.threadContext?.addSystemMessage === false) return [];
    const hasProcessor = configuredProcessors.some(p => !isProcessorWorkflow(p) && p.id === 'chat-channel-context');
    if (hasProcessor) return [];
    return [new ChatChannelProcessor()];
  }

  /**
   * Returns channel output processors that render the agent's stream to the
   * originating chat platform. The processor resolves its render context from
   * the inbound `requestContext` marker set by `processChatMessage` when
   * present, and otherwise reconstructs it from the run's thread via the bound
   * `AgentChannels` (so schedule / Studio / custom-UI runs on a channel-backed
   * thread still post back). Non-channel runs pass through untouched.
   *
   * Skipped if the user already added a processor with the same id.
   */
  /**
   * @deprecated No longer needed — `AgentChannels` no longer holds stateful resources that require cleanup.
   * Kept as a no-op for backwards compatibility with existing `ChannelProvider` implementations.
   */
  close(): void {
    // no-op
  }

  getOutputProcessors(configuredProcessors: OutputProcessorOrWorkflow[] = []): OutputProcessor[] {
    const hasProcessor = configuredProcessors.some(p => !isProcessorWorkflow(p) && p.id === 'chat-channel-render');
    if (hasProcessor) return [];
    return [new ChatChannelOutputProcessor(this)];
  }

  /**
   * Returns generic channel tools (add_reaction, remove_reaction) that resolve
   * the target adapter from the current request context.
   *
   * These are not injected into the agent automatically — pass them explicitly
   * if the agent should react to channel messages:
   *
   * ```ts
   * const agent = new Agent({
   *   channels,
   *   tools: { ...channels.getTools() },
   * });
   * ```
   *
   * Replies don't need a tool: the agent's response streams back to the
   * channel through the output processor.
   */
  getTools(): Record<string, unknown> {
    if (!this.toolsEnabled) return {};
    return this.makeChannelTools();
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  /**
   * Resolve the adapter for the current conversation from request context.
   */
  private getAdapterFromContext(context: { requestContext?: RequestContext }): { adapter: Adapter; threadId: string } {
    const channel = context.requestContext?.get('channel') as ChannelContext | undefined;
    if (!channel?.platform || !channel?.threadId) {
      throw new Error('No channel context — cannot determine platform or thread');
    }
    const adapter = this.adapters[channel.platform];
    if (!adapter) {
      throw new Error(`No adapter registered for platform "${channel.platform}"`);
    }
    return { adapter, threadId: channel.threadId };
  }

  /**
   * Derive the three per-event shapes we hand off to downstream systems from one set of
   * inputs. Keeping this in one place ensures the LLM (`attributes`), input processors
   * (`requestContext`), and memory (`metadata`) all see consistent author / thread facts.
   *
   *   - `channelContext` — goes on `requestContext` under the 'channel' key, consumed by
   *     `ChatChannelProcessor` and other input processors.
   *   - `attributes` — serialized as XML on the user message element the LLM sees (e.g. on
   *     `<user messageId=... authorId=... />`). Strings only.
   *   - `providerOptions` — written to the stored message's `content.providerMetadata`
   *     under `mastra.channels.<platform>` so UI/query callers can read author/channel
   *     facts off the message (e.g. show a Slack icon + author name) without unpacking
   *     the signal envelope. The LLM ignores `providerOptions.mastra.*` since only
   *     provider-keyed entries (openai, anthropic, …) are forwarded to the model.
   */
  /**
   * Resolve the external thread id to use when looking up a Mastra thread for
   * a tool-approval flow. Dispatches to per-platform compat shims that work
   * around quirks in how adapters surface threading on inbound action events.
   * Add new platform branches here as their compat shims land in `./compat/*`.
   */
  private resolveExternalThreadId(params: { platform: string; chatThread: Thread; messageId?: string }): string {
    const { platform, chatThread, messageId } = params;
    const adapter = this.adapters[platform];
    if (!adapter) return chatThread.id;

    switch (platform) {
      case 'slack':
        return (
          resolveSlackTopLevelThreadId({ platform, adapter, chatThreadId: chatThread.id, messageId }) ?? chatThread.id
        );
      default:
        return chatThread.id;
    }
  }

  /**
   * Resolve the bot's current profile display name through the adapter, once per platform and bot user.
   * Keyed by `botUserId` because one adapter can serve several installations, each with its own bot.
   * Adapters may report a stale handle as `userName` (e.g. Slack `auth.test` keeps the original
   * username after an app rename) while inbound mentions render the current display name.
   */
  private resolveBotDisplayName(platform: string): Promise<string | undefined> {
    const adapter = this.adapters[platform]!;
    const botUserId = adapter.botUserId;
    if (!botUserId || !adapter.getUser) return Promise.resolve(undefined);

    const key = `${platform}:${botUserId}`;
    const cached = this.botDisplayNames.get(key);
    if (cached) return cached;

    const pending = adapter.getUser(botUserId).then(
      user => {
        const name = user?.userName || user?.fullName || undefined;
        if (!name) this.botDisplayNames.delete(key);
        return name;
      },
      err => {
        this.botDisplayNames.delete(key);
        this.log('debug', `[${platform}] Failed to resolve bot display name`, err);
        return undefined;
      },
    );
    this.botDisplayNames.set(key, pending);
    return pending;
  }

  private async buildEventContext(params: {
    chatThread: Thread;
    platform: string;
    eventType: string;
    messageId: string | undefined;
    actor: { userId: string; userName?: string; fullName?: string; isBot?: boolean | 'unknown' };
  }): Promise<{
    channelContext: ChannelContext;
    attributes: Record<string, string | undefined>;
    providerOptions: MastraProviderMetadata;
  }> {
    const { chatThread, platform, eventType, messageId, actor } = params;
    const adapter = this.adapters[platform]!;
    const botUserId = adapter.botUserId;
    const botMention = botUserId ? chatThread.mentionUser(botUserId) : undefined;
    const actorName = actor.fullName || actor.userName;
    const actorMention = actor.userId ? chatThread.mentionUser(actor.userId) : undefined;
    const displayName = await this.resolveBotDisplayName(platform);
    const botDisplayName = displayName && displayName !== adapter.userName ? displayName : undefined;

    const channelContext: ChannelContext = {
      platform,
      eventType,
      isDM: chatThread.isDM,
      threadId: chatThread.id,
      channelId: chatThread.channelId,
      messageId,
      userId: actor.userId,
      userName: actorName,
      botUserId,
      botUserName: adapter.userName,
      ...(botDisplayName ? { botDisplayName } : {}),
      botMention,
    };

    // Attributes: short, flat, strings only — they're rendered as XML attrs on the signal.
    // In DMs the author is stable for the whole conversation (already in the system message),
    // so we keep this minimal to avoid noise on every turn.
    const attributes: Record<string, string | undefined> = { messageId };
    if (!chatThread.isDM) {
      attributes.authorName = actorName;
      attributes.authorId = actor.userId;
      attributes.authorMention = actorMention;
      if (actor.isBot) attributes.isBot = 'true';
    }

    const providerOptions: MastraProviderMetadata = {
      mastra: {
        channels: {
          [platform]: {
            ...(messageId !== undefined ? { messageId } : {}),
            author: {
              userId: actor.userId,
              ...(actor.userName !== undefined ? { userName: actor.userName } : {}),
              ...(actor.fullName !== undefined ? { fullName: actor.fullName } : {}),
              ...(actorMention !== undefined ? { mention: actorMention } : {}),
              ...(actor.isBot !== undefined ? { isBot: actor.isBot } : {}),
            },
          },
        },
      },
    };

    return { channelContext, attributes, providerOptions };
  }

  /**
   * Core handler wired to Chat SDK's onDirectMessage, onNewMention,
   * and onSubscribedMessage. Streams the Mastra agent response and
   * updates the channel message in real-time via edits.
   */
  private async handleChatMessage(
    chatThread: Thread,
    message: Message,
    mastra: Mastra,
    requestContext: RequestContext,
    signalMetadata: Record<string, unknown>,
    skipped: readonly Message[] = [],
  ): Promise<void> {
    // The SDK batches by conversation, not sender. Split the batch into runs of
    // consecutive messages from one sender so each run is dispatched under its
    // own author's identity, in the order they were sent. A message without a
    // userId is never grouped with another.
    const all = [...skipped, message];
    const batchIds = new Set(all.map(m => m.id));
    const runs: Message[][] = [];
    for (const m of all) {
      const last = runs[runs.length - 1];
      const userId = m.author?.userId;
      if (last && userId !== undefined && last[0]!.author?.userId === userId) last.push(m);
      else runs.push([m]);
    }
    for (const [i, run] of runs.entries()) {
      const runMessage = run[run.length - 1]!;
      // The final run holds the triggering message, so it uses the context the
      // handler saw. Earlier runs belong to other senders (or earlier turns) and
      // get a fresh context so per-sender data never leaks between runs.
      const isLast = i === runs.length - 1;
      try {
        await this.processChatMessage(
          chatThread,
          runMessage,
          mastra,
          isLast ? requestContext : new RequestContext(),
          isLast ? signalMetadata : {},
          run.slice(0, -1),
          batchIds,
        );
      } catch (err) {
        // One failed or refused run must not stop later senders' runs.
        await this.handleRunError(chatThread, runMessage, err);
      }
    }
  }

  private async handleRunError(chatThread: Thread, message: Message, err: unknown): Promise<void> {
    const error = err instanceof Error ? err : new Error(String(err));
    // A refused request is not a malfunction: the host decided this sender
    // gets nothing. Log it and stop — posting would echo the host's
    // authorization message into the chat thread and confirm the bot is
    // present to a sender who was just turned away.
    if (err instanceof ChannelSessionRejectedError) {
      this.log('info', `[${chatThread.adapter.name}] Session resolver refused the message`, {
        messageId: message.id,
        authorId: message.author?.userId,
        reason: error.message,
      });
      return;
    }
    let loggedError;
    try {
      const message = extractErrorMessage(err);
      const cause =
        typeof err === 'object' && err !== null && 'cause' in err ? extractErrorMessage(err.cause) : undefined;
      const { id, domain, category } = err instanceof MastraError ? err : {};
      loggedError = {
        message: typeof message === 'string' && message.length > 0 ? message : 'Unknown error',
        ...(typeof id === 'string' ? { code: id } : {}),
        ...(typeof domain === 'string' ? { domain } : {}),
        ...(typeof category === 'string' ? { category } : {}),
        ...(typeof cause === 'string' && cause.length > 0 ? { cause: { message: cause } } : {}),
      };
    } catch {
      loggedError = { message: 'Error details unavailable' };
    }
    const diagnostic = {
      platform: chatThread.adapter.name,
      threadId: chatThread.id,
      messageId: message.id,
      authorId: message.author?.userId,
      error: loggedError,
    };
    this.log('error', `[${chatThread.adapter.name}] Error handling message ${JSON.stringify(diagnostic)}`, diagnostic);
    try {
      const adapterConfig = this.adapterConfigs[chatThread.adapter.name];
      const errorMessage = adapterConfig?.formatError ? adapterConfig.formatError(error) : `❌ Error: ${error.message}`;
      await chatThread.post(errorMessage);
    } catch (postErr) {
      this.log('debug', 'Failed to post error message to thread', postErr);
    }
  }

  private async processChatMessage(
    chatThread: Thread,
    message: Message,
    mastra: Mastra,
    requestContext: RequestContext,
    signalMetadata: Record<string, unknown> = {},
    skipped: readonly Message[] = [],
    historyExcludeIds: ReadonlySet<string> = new Set([...skipped, message].map(m => m.id)),
  ): Promise<void> {
    const platform = chatThread.adapter.name;
    // Messages batched by a concurrency strategy, oldest first, then the current one.
    const batch = [...skipped, message].filter(m => !this.isContentlessMessage(m));

    // Some adapters lift platform side-channel events (read receipts, delivery
    // acks) into inbound messages carrying no text and no attachments. Running
    // the agent on nothing still produces a reply, which produces another
    // receipt, which wakes the agent again — a self-sustaining loop. There is
    // nothing to answer here, so drop it before any thread, memory, or run
    // work happens. Custom handlers run ahead of this and still see the
    // message if they want it.
    if (batch.length === 0) {
      this.log('debug', `[${platform}] Skipping message with no text and no attachments`, {
        messageId: message.id,
      });
      return;
    }

    // Map to a Mastra thread for memory/history.
    // chatThread.id encodes channel + threadTs, so it's stable per conversation:
    // each Slack thread (including top-level DM, DM thread reply, channel mention, and
    // channel thread reply) gets its own mastra thread.
    const externalThreadId = chatThread.id;
    const defaultResourceId = `${platform}:${message.author.userId}`;
    const mastraThread = await this.getOrCreateThread({
      externalThreadId,
      channelId: chatThread.channelId,
      platform,
      // Lazily resolved: the hook only runs when we're actually creating a new
      // thread, never when reusing an existing one (which keeps its stored owner).
      resourceId: this.resolveChannelResourceId({ platform, chatThread, message, defaultResourceId }),
      // Same laziness for the thread id hook; it runs after the resourceId
      // resolves so hosts can align the two (e.g. thread id = session id).
      threadId: this.resolveThreadId
        ? (resourceId: string, defaultThreadId: string) =>
            this.resolveThreadId!({ platform, thread: chatThread, message, resourceId, defaultThreadId })
        : undefined,
      mastra,
    });

    // Use the thread's resourceId for memory, not the current message author.
    // In multi-user threads (e.g. Slack channels), the thread is owned by whoever
    // started it. Other participants' messages are still part of that thread's history.
    const threadResourceId = mastraThread.resourceId;

    // Fetch recent thread history when configured, this is a non-DM mention,
    // AND the agent isn't already subscribed to this thread. If subscribed,
    // the agent already has history via Mastra's memory system.
    // History is prepended to the user message text (not as a separate message)
    // to avoid consecutive user messages which some providers reject (e.g. DeepSeek).
    let historyBlock: string | undefined; // TODO: convert platform thread chat history into Mastra messages instead of one big text block
    const maxMessages = this.threadContext.maxMessages ?? 10;
    if (maxMessages > 0 && !chatThread.isDM) {
      const alreadySubscribed = await chatThread.isSubscribed();
      if (!alreadySubscribed) {
        this.logger?.debug?.(`Fetching thread history (max ${maxMessages}) for first mention in ${chatThread.id}`);
        const history = await this.fetchThreadHistory(chatThread, historyExcludeIds, maxMessages);
        this.logger?.debug?.(`Fetched ${history.length} messages from thread history`);
        if (history.length > 0) {
          const lines = ['[Thread context — messages in this thread before you joined]'];
          for (const msg of history) {
            const mention = msg.userId ? chatThread.mentionUser(msg.userId) : undefined;
            let prefix = mention ? (msg.author ? `${msg.author} (${mention})` : mention) : msg.author;
            if (msg.isBot) prefix += ' (bot)';
            lines.push(`[${prefix}] (msg:${msg.id}): ${msg.text}`);
          }
          historyBlock = lines.join('\n');
        }
      } else {
        this.logger?.debug?.(`Skipping thread history fetch — already subscribed to ${chatThread.id}`);
      }
    }

    const messageTexts = batch.map(m => {
      const richText = m.formatted ? chatModule().stringifyMarkdown(m.formatted).trim() : undefined;
      return richText || m.text;
    });
    const text = [historyBlock, ...messageTexts].filter(Boolean).join('\n\n');
    const parts: Exclude<AgentSignalContents, string> = [{ type: 'text', text }];
    const attachments = batch.flatMap(m => m.attachments ?? []).filter(a => a.url || a.fetchData);

    // Route attachments based on `inlineMedia` config (see DEFAULT_INLINE_MEDIA_TYPES).
    // Inline types are sent as file parts (the LLM adapter converts image/* to
    // image content automatically). Non-inline types are described as text
    // metadata so the agent is aware of them without crashing models that
    // reject unsupported media (e.g. OpenAI rejects video/mp4).
    this.logger?.debug('[CHANNEL] Attachments', {
      count: attachments.length,
      attachments: attachments.map(a => ({
        type: a.type,
        mimeType: a.mimeType,
        url: a.url,
        hasData: !!a.fetchData,
      })),
    });
    for (const att of attachments) {
      if (!att.url && !att.fetchData) continue;
      const mimeType = att.mimeType || (att.type === 'image' ? 'image/png' : undefined);
      if (!mimeType) continue;

      const inline = this.shouldInline(mimeType);
      const filename = att.name || att.url?.split('/').pop() || 'file';
      if (inline) {
        let data: string | undefined;
        let fetchFailed = false;
        if (att.fetchData) {
          // Prefer authenticated fetch (e.g. Slack CDN requires auth)
          try {
            const buf = await att.fetchData();
            const bytes = buf instanceof ArrayBuffer ? new Uint8Array(buf) : buf;
            const base64 = Buffer.from(bytes).toString('base64');
            data = `data:${mimeType};base64,${base64}`;
          } catch (err) {
            this.logger?.warn('[CHANNEL] fetchData failed', { mimeType, error: String(err) });
            fetchFailed = true;
          }
        } else {
          // Public URL (e.g. Discord CDN) — let the provider fetch directly
          data = att.url;
        }
        if (data) {
          parts.push({
            type: 'text',
            text: `[Attached ${mimeType} file${att.name ? `: ${att.name}` : ''}]`,
          });
          parts.push({
            type: 'file',
            data,
            mediaType: mimeType,
            ...(att.name ? { filename: att.name } : {}),
          });
        } else if (fetchFailed) {
          parts.push({
            type: 'text',
            text: `[Attachment unavailable: ${filename} (${mimeType}) — the file could not be loaded, it may have been deleted before processing]`,
          });
        }
      } else {
        parts.push({
          type: 'text',
          text: `[Attached file: ${filename} (${mimeType})${att.url ? ` — ${att.url}` : ''}]`,
        });
      }
    }

    // Promote URLs in message text to file parts based on `inlineLinks` config.
    if (this.inlineLinkRules && text) {
      const urls = extractUrls(text);
      for (const url of urls) {
        const rule = findInlineLinkRule(url, this.inlineLinkRules);
        if (!rule) continue;

        if (rule.forcedMimeType) {
          // Object entry with forced mime type — skip HEAD, always promote.
          parts.push({ type: 'file', data: url, mediaType: rule.forcedMimeType });
        } else {
          // String entry — HEAD to determine Content-Type, then check inlineMedia.
          const contentType = await headContentType(url, this.logger);
          if (contentType && this.shouldInline(contentType)) {
            parts.push({ type: 'file', data: url, mediaType: contentType });
          }
        }
      }
    }

    // Route the message through the agent's signal pipeline. The subscription is opened
    // lazily on first message per Mastra thread so any signals — ours or others sent to the
    // same thread — render through a single consumer. sendSignal then either delivers the
    // message into an already-running agent loop or wakes the thread with an idle stream
    // using the same options we used to pass to agent.stream().
    const adapterConfig = this.adapterConfigs[platform];
    // Auto-approve suspended tools when there's no way to render an
    // approval card with buttons. Block Kit cards have buttons; plain
    // `'text'` mode has only a "reply approve/deny" hint with no
    // first-class affordance, so we auto-approve to avoid getting stuck.
    const { resolved: toolDisplay, fn: toolDisplayFn } = this.resolveToolDisplay(
      platform,
      adapterConfig?.toolDisplay,
      this.resolveStreaming(adapterConfig?.streaming).enabled,
      adapterConfig?.cards,
      adapterConfig?.formatToolCall,
    );
    const canRenderApprovalButtons =
      toolDisplayFn !== undefined ||
      toolDisplay === 'cards' ||
      toolDisplay === 'timeline' ||
      toolDisplay === 'grouped' ||
      // `'hidden'` still posts approval cards, but only adapters with
      // interactive buttons can act on them. Button-less surfaces (SMS,
      // iMessage, custom gateways) must auto-resume or the thread gets stuck.
      (toolDisplay === 'hidden' && (adapterConfig?.approvalButtons ?? APPROVAL_BUTTON_PLATFORMS.has(platform)));

    this.log('info', '[processChatMessage] tool approval config', {
      platform,
      toolDisplay,
      toolDisplayFn: !!toolDisplayFn,
      canRenderApprovalButtons,
      autoResumeSuspendedTools: canRenderApprovalButtons ? undefined : true,
    });

    const { channelContext, attributes, providerOptions } = await this.buildEventContext({
      chatThread,
      platform,
      eventType: chatThread.isDM ? 'message' : 'mention',
      messageId: message.id,
      actor: message.author,
    });

    // NOTE: `requestContext` is constructed per message at the handler boundary
    // (see `beginMessage` in initialize) so a custom handler can contribute to
    // it — e.g. stamping the tenant — before the run dispatches. Core only
    // enriches it here.
    requestContext.set('channel', channelContext);

    // Stash the per-event render deps so `ChatChannelOutputProcessor` can
    // route the agent's stream to the chat platform. The processor opens an
    // async queue on the first chunk and hands the iterable to the existing
    // streaming/static driver. This replaces the previous per-thread
    // subscription consumer: rendering now happens inline with the run that
    // produces the chunks, so only the Lambda that won the wake race
    // (signals reservation) renders the reply.
    const renderContext = this._buildRenderContext(chatThread, platform, { requesterId: message.author?.userId });
    requestContext.set(CHAT_CHANNEL_RENDER_CONTEXT_KEY, renderContext);

    void chatThread.subscribe().catch(err => {
      this.log('debug', 'chatThread.subscribe failed', err);
    });

    // When the message is text-only, pass the bare string to the signal pipeline.
    // Otherwise pass the parts array directly — both shapes match AgentSignalContents.
    const signalContents: AgentSignalContents = parts.length === 1 && parts[0]?.type === 'text' ? parts[0].text : parts;

    await this.dispatchInboundMessage({
      signalContents,
      attributes,
      signalMetadata,
      providerOptions,
      requestContext,
      thread: mastraThread,
      memory: {
        thread: mastraThread.id,
        resource: threadResourceId,
      },
      autoResumeSuspendedTools: canRenderApprovalButtons ? undefined : true,
    });
  }

  /** A message with neither text nor attachments gives the agent nothing to run on. */
  private isContentlessMessage(message: Message): boolean {
    if (message.attachments?.length) return false;
    if (message.text?.trim()) return false;
    const richText = message.formatted ? chatModule().stringifyMarkdown(message.formatted).trim() : '';
    return !richText;
  }

  /**
   * Fetch recent messages from the platform thread to provide context.
   * Returns messages in chronological order (oldest first), excluding the
   * current triggering message.
   */
  private async fetchThreadHistory(
    chatThread: Thread,
    excludeIds: ReadonlySet<string>,
    maxMessages: number,
  ): Promise<ThreadHistoryMessage[]> {
    const messages: ThreadHistoryMessage[] = [];

    try {
      // chatThread.messages is an async iterator that yields newest-first
      for await (const msg of chatThread.messages) {
        // Skip the messages that triggered this request
        if (excludeIds.has(msg.id)) continue;

        const historyText = msg.formatted ? chatModule().stringifyMarkdown(msg.formatted).trim() : undefined;
        messages.push({
          id: msg.id,
          author: msg.author.fullName || msg.author.userName || 'Unknown',
          userId: msg.author.userId,
          text: historyText || msg.text,
          isBot: msg.author.isBot === true,
        });

        if (messages.length >= maxMessages) break;
      }
    } catch (err) {
      this.logger?.warn?.(`Failed to fetch thread history: ${err}`);
      return [];
    }

    // Reverse to get chronological order (oldest first)
    return messages.reverse();
  }

  /**
   * Build the per-event render dependencies stashed on `requestContext` for
   * `ChatChannelOutputProcessor`. Captures the adapter, driver mode,
   * tool-display config, approval-card stash callbacks, and the typing-status
   * wrapper as a callable so the processor can apply it after the queue is
   * created. The returned object is plain data — no streams, no promises —
   * so it's safe to stash on `requestContext` for the processor to read later.
   *
   * @internal Used by `processChatMessage` and the approve/decline paths.
   */
  _buildRenderContext(
    chatThread: Thread,
    platform: string,
    {
      approvalContext,
      requesterId,
    }: { approvalContext?: { toolCallId: string; messageId: string }; requesterId?: string } = {},
  ): ChatChannelRenderContext {
    const adapter = this.adapters[platform]!;
    const adapterConfig = this.adapterConfigs[platform];
    const streaming = this.resolveStreaming(adapterConfig?.streaming);
    const { resolved: toolDisplay, fn: toolDisplayFn } = this.resolveToolDisplay(
      platform,
      adapterConfig?.toolDisplay,
      streaming.enabled,
      adapterConfig?.cards,
      adapterConfig?.formatToolCall,
    );

    const typingGate = { active: false };

    const onApprovalPosted = (toolCallId: string, record: PendingApprovalRecord) => {
      this.pendingApprovalCards.set(toolCallId, { ...record, requesterId: record.requesterId ?? requesterId });
    };
    const getPendingApproval = (id: string) => this.pendingApprovalCards.get(id);
    const takePendingApproval = (id: string) => {
      const r = this.pendingApprovalCards.get(id);
      if (r) this.pendingApprovalCards.delete(id);
      return r;
    };

    return {
      adapter,
      chatThread,
      platform,
      streaming,
      toolDisplay,
      toolDisplayFn,
      channelToolNames: this.channelToolNames,
      logger: this.logger,
      onApprovalPosted,
      getPendingApproval,
      takePendingApproval,
      wrapStream: stream => this.withTypingStatus(stream, chatThread, platform, adapterConfig, typingGate),
      typingGate,
      formatError: adapterConfig?.formatError,
      textFormat: adapterConfig?.textFormat,
      onAbort: adapterConfig?.onAbort,
      approvalContext,
    };
  }

  /**
   * Whether a `tool-call-approval` chunk for `toolName` should render
   * Approve/Deny controls in the chat. The base class always renders them;
   * subclasses that resolve approval policy themselves (e.g. an agent
   * controller auto-approving `allow` tools) return `false` when no human
   * decision is actually pending.
   *
   * @internal
   */
  async shouldRenderToolApproval(_requestContext: RequestContext | undefined, _toolName: string): Promise<boolean> {
    return true;
  }

  /**
   * Reconstruct a {@link ChatChannelRenderContext} for a Mastra thread that is
   * backed by a channel, without an inbound platform event.
   *
   * The inbound webhook paths (`processChatMessage`, approve/decline) stash a
   * render context on `requestContext` because they already hold the live
   * `Thread` handle from `event.thread`. Runs that did NOT originate from a
   * platform message (schedule fires, Studio, custom UI, user code) have no such
   * handle, so `ChatChannelOutputProcessor` calls this to rebuild it from the
   * thread's persisted channel coordinates.
   *
   * Returns `null` when the thread is not channel-backed (no `channel_platform`
   * metadata) or its platform adapter isn't configured on this instance — the
   * processor then passes the run through untouched.
   *
   * Delegates to the same {@link _buildRenderContext} used by the inbound paths,
   * so both paths produce an identical render context (single source of truth).
   * The only per-fire inputs are `platform` (a persisted string) and the live
   * `Thread` handle, which the Chat SDK materializes from the stored external id
   * via `chat.thread(externalThreadId)`.
   */
  async buildRenderContextForThread(threadId: string): Promise<ChatChannelRenderContext | null> {
    const mastra = this.getMastra();
    const storage = mastra?.getStorage();
    if (!storage) return null;

    const memoryStore = await storage.getStore('memory');
    if (!memoryStore) return null;

    const thread = await memoryStore.getThreadById({ threadId });
    if (!thread) return null;

    const platform = thread.metadata?.channel_platform;
    const externalThreadId = thread.metadata?.channel_externalThreadId;
    if (typeof platform !== 'string' || platform.length === 0) return null;
    if (typeof externalThreadId !== 'string' || externalThreadId.length === 0) return null;
    if (!this.adapters[platform]) return null;

    const chat = this.chat;
    if (!chat) return null;

    const chatThread = chat.thread(externalThreadId);
    return this._buildRenderContext(chatThread, platform);
  }

  /**
   * Normalize the per-adapter `streaming` option (`boolean | { updateIntervalMs? }`)
   * into a flat `{ enabled, options }` shape so call-sites don't have to
   * re-derive both from the raw union.
   */
  private resolveStreaming(raw: StreamingConfig | undefined): {
    enabled: boolean;
    options?: { updateIntervalMs?: number };
  } {
    if (raw === undefined || raw === false) return { enabled: false };
    if (raw === true) return { enabled: true, options: {} };
    return { enabled: true, options: raw };
  }

  /**
   * Pass-through async generator that yields chunks unchanged but emits
   * typing-status updates (`startTyping`) along the way. Lives outside the
   * drivers so both drivers benefit from the same dedup + gate logic.
   *
   * The streaming driver flips `typingGate.active = true` while a
   * `StreamingPlan` post is in flight — Slack's `assistant.threads.setStatus`
   * (what `startTyping` maps to) only auto-clears on `chat.postMessage`, not
   * on `chat.stopStream`, so a status set during streaming would stick after
   * the run ends. The static driver leaves the gate `false` so typing works
   * normally in cards/hidden modes.
   *
   * When the run's stream ends, an empty status is sent to clear any status
   * this run set, so runs that end without posting a message (e.g. terminated
   * by `stopWhen` on a tool call, or aborted) don't leave a stale status
   * pinned to the thread.
   */
  private async *withTypingStatus(
    stream: AsyncIterable<AgentChunkType<any>>,
    chatThread: Thread,
    platform: string,
    adapterConfig: ChannelAdapterConfig | undefined,
    typingGate: { active: boolean },
  ): AsyncGenerator<AgentChunkType<any>> {
    const typingStatusOption = adapterConfig?.typingStatus;
    const typingStatusFn: TypingStatusFn | null =
      typingStatusOption === false
        ? null
        : typeof typingStatusOption === 'function'
          ? typingStatusOption
          : defaultTypingStatus;

    let currentTypingStatus: string | undefined;
    let statusSent = false;

    try {
      for await (const chunk of stream) {
        if (typingStatusFn && !typingGate.active) {
          let result: ReturnType<TypingStatusFn>;
          try {
            const ctx: TypingStatusContext = {
              platform,
              threadId: chatThread.id,
              currentStatus: currentTypingStatus,
              channelTools: this.channelToolNames,
            };
            result = typingStatusFn(chunk, ctx);
          } catch (e) {
            this.logger?.debug('[CHANNEL] typingStatus function threw (continuing)', { error: e });
            result = undefined;
          }
          if (typeof result === 'string' && result.length > 0 && result !== currentTypingStatus) {
            currentTypingStatus = result;
            statusSent = true;
            chatThread.startTyping(result).catch(e => {
              this.logger?.debug('[CHANNEL] Typing indicator failed (best-effort)', { error: e });
            });
          }
        }
        // Reset the dedup state on per-step run boundaries so the next step can
        // re-emit its first status even if it matches the previous step's last
        // status.
        if (chunk.type === 'finish' || chunk.type === 'error' || chunk.type === 'abort') {
          currentTypingStatus = undefined;
        }
        yield chunk;
      }
    } finally {
      // End of the run's stream (the session queue closed on the terminal
      // step-finish / error / abort, or the driver stopped consuming). Slack's
      // `assistant.threads.setStatus` only auto-clears on `chat.postMessage`,
      // so a run that set a status but never posted a message would leave it
      // pinned on the thread indefinitely. Send an empty status to clear it —
      // a no-op when a post already cleared it. Skip the clear while the
      // streaming gate is active: the in-flight streaming post clears the
      // status when it completes.
      if (statusSent && !typingGate.active) {
        chatThread.startTyping('').catch(e => {
          this.logger?.debug('[CHANNEL] Typing clear failed (best-effort)', { error: e });
        });
      }
    }
  }

  /**
   * Look up a channel-backed Mastra thread and retain the store/metadata needed
   * by the create path when no mapping exists.
   */
  private async findThreadMapping({
    externalThreadId,
    channelId,
    platform,
    mastra,
  }: {
    externalThreadId: string;
    channelId: string;
    platform: string;
    mastra: Mastra;
  }) {
    const storage = mastra.getStorage();
    if (!storage) {
      throw new Error('Storage is required for channel thread mapping. Configure storage in your Mastra instance.');
    }

    const memoryStore = await storage.getStore('memory');
    if (!memoryStore) {
      throw new Error(
        'Memory store is required for channel thread mapping. Configure storage in your Mastra instance.',
      );
    }

    const legacyMetadata = {
      channel_platform: platform,
      channel_externalThreadId: externalThreadId,
      channel_externalChannelId: channelId,
    };

    const ownerId = this.getOwnerId();
    if (ownerId === null) {
      // No owner bound yet - scoping is impossible; behave exactly as before
      // and never stamp a null owner id.
      const { threads } = await memoryStore.listThreads({
        filter: { metadata: legacyMetadata },
        perPage: 1,
      });
      return { thread: threads[0], memoryStore, metadata: legacyMetadata };
    }

    const metadata = { ...legacyMetadata, channel_ownerId: ownerId };

    // Primary lookup: threads already scoped to this agent.
    const { threads: scoped } = await memoryStore.listThreads({
      filter: { metadata },
      perPage: 1,
    });
    if (scoped[0]) return { thread: scoped[0], memoryStore, metadata };

    // Legacy fallback: pre-upgrade threads carry no channel_ownerId. Metadata
    // filters match subsets, so this query also returns threads claimed by
    // OTHER agents - post-filter to unclaimed rows only, oldest first so
    // adoption deterministically picks the original thread. If a conversation
    // somehow accumulates more than 10 candidate rows, an unclaimed one past
    // the page could be missed and a fresh thread created - acceptable
    // degradation.
    const { threads: candidates } = await memoryStore.listThreads({
      filter: { metadata: legacyMetadata },
      perPage: 10,
      orderBy: { field: 'createdAt', direction: 'ASC' },
    });
    const unclaimed = candidates.find(candidate => {
      const candidateMeta = (candidate.metadata ?? {}) as Record<string, unknown>;
      return !('channel_ownerId' in candidateMeta);
    });
    if (unclaimed) {
      // Lazily adopt the legacy thread: the first agent to touch it claims it
      // by stamping its own id, preserving all existing metadata.
      const claimed = await memoryStore.patchThread({
        id: unclaimed.id,
        metadata: { ...((unclaimed.metadata ?? {}) as Record<string, unknown>), channel_ownerId: ownerId },
      });
      return { thread: claimed, memoryStore, metadata };
    }

    return { thread: undefined, memoryStore, metadata };
  }

  /**
   * Resolves an existing Mastra thread for the given external IDs, or creates one.
   */
  private async getOrCreateThread({
    externalThreadId,
    channelId,
    platform,
    resourceId,
    threadId,
    mastra,
  }: {
    externalThreadId: string;
    channelId: string;
    platform: string;
    /**
     * The owner for a newly-created thread. Pass a function to defer resolution
     * until we know a new thread is actually needed; it is never called when an
     * existing thread is reused.
     */
    resourceId: string | (() => string | Promise<string>);
    /**
     * The id for a newly-created thread, resolved lazily after the owner —
     * never called when an existing thread is reused (which keeps its id).
     * Omitted: a random UUID.
     */
    threadId?: (resolvedResourceId: string, defaultThreadId: string) => string | Promise<string>;
    mastra: Mastra;
  }): Promise<StorageThreadType> {
    const {
      thread: existingThread,
      memoryStore,
      metadata,
    } = await this.findThreadMapping({
      externalThreadId,
      channelId,
      platform,
      mastra,
    });
    if (existingThread) return existingThread;

    const resolvedResourceId = typeof resourceId === 'function' ? await resourceId() : resourceId;
    const defaultThreadId = crypto.randomUUID();
    let resolvedThreadId = threadId ? await threadId(resolvedResourceId, defaultThreadId) : defaultThreadId;

    // saveThread upserts by id, so a resolver id that already belongs to another
    // thread would overwrite it. Fall back to the generated id instead.
    if (resolvedThreadId && resolvedThreadId !== defaultThreadId) {
      const existing = await memoryStore.getThreadById({ threadId: resolvedThreadId }).catch(() => null);
      if (existing) {
        this.log(
          'warn',
          `resolveThreadId returned "${resolvedThreadId}" which already belongs to an existing thread; using a generated id instead`,
        );
        resolvedThreadId = defaultThreadId;
      }
    }

    return memoryStore.saveThread({
      thread: {
        id: resolvedThreadId || defaultThreadId,
        title: `${platform} conversation`,
        resourceId: resolvedResourceId,
        createdAt: new Date(),
        updatedAt: new Date(),
        metadata,
      },
    });
  }

  /**
   * Generate generic channel tools that resolve the adapter from request context.
   * Tool names are platform-agnostic (e.g. `add_reaction`, not `discord_add_reaction`).
   */
  private makeChannelTools() {
    return {
      add_reaction: createTool({
        id: 'add_reaction',
        description: 'Add an emoji reaction to a message.',
        inputSchema: z.object({
          messageId: z.string().describe('The ID of the message to react to'),
          emoji: z.string().describe('The emoji to react with (e.g. "thumbsup")'),
        }),
        execute: async ({ messageId, emoji }, context) => {
          const { adapter, threadId } = this.getAdapterFromContext(context);
          await adapter.addReaction(threadId, messageId, emoji);
          return { ok: true };
        },
      }),

      remove_reaction: createTool({
        id: 'remove_reaction',
        description: 'Remove an emoji reaction from a message.',
        inputSchema: z.object({
          messageId: z.string().describe('The ID of the message to remove reaction from'),
          emoji: z.string().describe('The emoji to remove'),
        }),
        execute: async ({ messageId, emoji }, context) => {
          const { adapter, threadId } = this.getAdapterFromContext(context);
          await adapter.removeReaction(threadId, messageId, emoji);
          return { ok: true };
        },
      }),
    };
  }

  /**
   * Persistent reconnection loop for Gateway-based adapters (e.g. Discord).
   */
  private startGatewayLoop(
    name: string,
    startGateway: (options: { waitUntil: (p: Promise<unknown>) => void }, durationMs?: number) => Promise<Response>,
  ): void {
    const DURATION = 24 * 60 * 60 * 1000;
    const RETRY_DELAY = 5000;

    const reconnect = async () => {
      while (true) {
        try {
          let resolve: () => void;
          let reject: (err: unknown) => void;
          const done = new Promise<void>((res, rej) => {
            resolve = res;
            reject = rej;
          });
          await startGateway(
            {
              waitUntil: (p: Promise<unknown>) => {
                void p.then(
                  () => resolve!(),
                  err => reject!(err),
                );
              },
            },
            DURATION,
          );
          await done;
          this.log('info', `[${name}] Gateway session ended, reconnecting...`);
        } catch (err) {
          this.log('error', `[${name}] Gateway error, retrying in ${RETRY_DELAY / 1000}s`, err);
          await new Promise(r => setTimeout(r, RETRY_DELAY));
        }
      }
    };

    void reconnect();
  }

  /**
   * Resolve the tool-display mode for a run.
   *
   *  - `'timeline'` / `'grouped'` push `task_update` chunks into a streaming
   *    Plan widget, so they require `streaming: true`. Without streaming we
   *    fall back to `'cards'`.
   *  - `'cards'` posts discrete Block-Kit cards via `chatThread.post`/`edit`,
   *    which the streaming driver doesn't render (everything inside a
   *    `StreamingPlan` post is one message). With streaming enabled we fall
   *    back to `'timeline'`.
   *
   * Both fallbacks log a one-time warning per platform so the misconfiguration
   * is visible without spamming on every run.
   */
  private resolveToolDisplay(
    platform: string,
    requested: ToolDisplay | undefined,
    streamingEnabled: boolean,
    deprecatedCards?: boolean,
    deprecatedFormatToolCall?: (info: {
      toolName: string;
      args: Record<string, unknown>;
      result: unknown;
      isError?: boolean;
    }) => PostableMessage | null,
  ): { resolved: 'cards' | 'text' | 'timeline' | 'grouped' | 'hidden'; fn?: ToolDisplayFn } {
    // Function form: drivers call the fn directly. The resolved mode is
    // the default `'cards'` — drivers use it only for any event the fn
    // doesn't render (returns `undefined`).
    let fn = typeof requested === 'function' ? requested : undefined;
    const requestedMode = typeof requested === 'function' ? undefined : requested;
    // Deprecated `cards: boolean` only applies when `toolDisplay` is not set
    // (in any form — string mode or function): `cards: true` → `'cards'`,
    // `cards: false` → `'text'`. The `@deprecated` JSDoc surfaces in IDEs so
    // we don't bother with a runtime warning. The discriminated union also
    // makes `cards` + `toolDisplay` a type error, but we still guard at
    // runtime so casts/JS callers don't get surprising fallback behavior
    // when the fn returns `undefined`.
    const fromDeprecatedCards =
      requested === undefined && deprecatedCards !== undefined ? (deprecatedCards ? 'cards' : 'text') : undefined;
    // Deprecated `formatToolCall` is shimmed into a `ToolDisplayFn`. The old
    // callback only fired on `tool-result`/`tool-error` and returned a
    // message (or `null` to skip), so the shim mirrors that contract: emit
    // `{ kind: 'post', message }` for those two events and `undefined` for
    // everything else so the built-in renderer handles the `running` /
    // `approval` events.
    if (!fn && deprecatedFormatToolCall) {
      fn = event => {
        if (event.kind !== 'result' && event.kind !== 'error') return undefined;
        const value = event.kind === 'result' ? event.result : event.error;
        const message = deprecatedFormatToolCall({
          toolName: event.toolName,
          args: (event.args ?? {}) as Record<string, unknown>,
          result: value,
          isError: event.kind === 'error' ? true : event.isError,
        });
        if (message == null) return undefined;
        return { kind: 'post', message };
      };
    }
    // Default is always `'cards'`: `'timeline'`/`'grouped'` need
    // `StreamingPlan` (not supported on every platform) so users opt in
    // explicitly. `'cards'` works under both streaming and static modes
    // — the streaming driver closes the session, posts the card, and
    // reopens on the next chunk.
    const toolDisplay = requestedMode ?? fromDeprecatedCards ?? 'cards';

    // `'timeline'` and `'grouped'` push `task_update`/`plan_update` chunks
    // that only render inside a chat-SDK `StreamingPlan`. Without streaming
    // there's no Plan to push into, so warn and fall back to `'cards'`.
    // `'cards'` and `'text'` work under both streaming and static modes:
    // the streaming driver closes the session, posts the per-tool message,
    // and reopens on the next chunk — same lifecycle as a `ToolDisplayFn`
    // returning `{ kind: 'post' }`.
    const isStreamingOnlyMode = toolDisplay === 'timeline' || toolDisplay === 'grouped';
    if (isStreamingOnlyMode && !streamingEnabled) {
      if (!this.warnedToolDisplayFallback.has(platform)) {
        this.warnedToolDisplayFallback.add(platform);
        this.log(
          'warn',
          `[${platform}] toolDisplay: '${toolDisplay}' requires streaming: true; falling back to 'cards'.`,
        );
      }
      return { resolved: 'cards', fn };
    }
    return { resolved: toolDisplay, fn };
  }

  protected log(level: 'info' | 'warn' | 'error' | 'debug', message: string, ...args: unknown[]): void {
    if (!this.logger) return;
    if (level === 'error') {
      this.logger.error(message, { args });
    } else if (level === 'warn') {
      this.logger.warn(message, { args });
    } else if (level === 'debug') {
      this.logger.debug(message, { args });
    } else {
      this.logger.info(message, { args });
    }
  }
}
